import { useContext, useEffect, useState } from 'react'
import { GridItemChangeEvent } from '@progress/kendo-react-grid'

import { ToastContext } from '../Toast/Toast'
import { NoteValue } from './types'

interface NoteStateProps<T> {
  notes?: NoteValue<T>[]
  forceRefresh(): void
  onAdd(item: T): void
  onEdit(item: NoteValue<T>): void
  onUpdate(item: T): void
  onRemove(item: T): void
}

interface NoteState<T> {
  add: (dataItem: NoteValue<T>) => Promise<void>
  update: (dataItem: NoteValue<T>) => Promise<void>
  remove: (dataItem: NoteValue<T>) => Promise<void>
  edit: (item: NoteValue<T>) => void
  discard: (dataItem: NoteValue<T>) => void
  addNew: (dataItem: NoteValue<T>) => void
  itemChange: (event: GridItemChangeEvent) => void
  cancel: () => void
  isLoading: boolean
  notes: NoteValue<T>[]
  setNotes: React.Dispatch<React.SetStateAction<NoteValue<T>[]>>
  setNote: (dataItem: NoteValue<T>) => void
  setEditNote: (dataItem: NoteValue<T>) => void
}

export function useNoteState<T>(props: NoteStateProps<T>): NoteState<T> {
  const [notes, setNotes] = useState<NoteValue<T>[]>([])
  const setToast = useContext(ToastContext)

  useEffect(() => {
    if (props.notes) {
      setNotes(props.notes)
    }
  }, [props.notes]) // eslint-disable-line react-hooks/exhaustive-deps

  const setNote = (dataItem: NoteValue<T>) => {
    setNotes(notes.map((x) => (x?.id === dataItem.id ? dataItem : x)))
  }

  const setEditNote = (dataItem: NoteValue<T>) => {
    setNote({ ...dataItem, inEdit: true })
  }

  const handleAdd = async (dataItem: NoteValue<T>) => {
    if (dataItem?.description) {
      await props.onAdd(dataItem)
      setToast({ type: 'success', title: 'Note has been added.' })
      props.forceRefresh()
    }
  }

  const handleUpdate = async (dataItem: NoteValue<T>) => {
    await props.onUpdate(dataItem)
    setToast({ type: 'success', title: 'Note has been updated.' })
    props.forceRefresh()
  }

  const handleRemove = async (dataItem: NoteValue<T>) => {
    if (dataItem?.id) {
      await props.onRemove(dataItem)
      setToast({ type: 'success', title: 'Note has been removed.' })
      props.forceRefresh()
    }
  }

  const handleCancel = () => {
    setNotes(notes.map((note) => ({ ...note, inEdit: false })))
  }

  const handleDiscard = (dataItem: NoteValue<T>) => {
    setNotes(removeItem(notes, dataItem))
  }

  const removeItem = (items: NoteValue<T>[], item: NoteValue<T>) => {
    return items.filter((x) => x.id !== item.id)
  }

  const handleItemChange = (event: GridItemChangeEvent) => {
    setNotes(
      notes.map((x) =>
        x.id === event.dataItem.id ? { ...x, [event.field as string]: event.value } : x
      )
    )
  }

  const handleAddNew = (dataItem: NoteValue<T>) => {
    const itemsAddedWithoutId = notes.filter((x) => !x.id).length
    if (itemsAddedWithoutId === 0) {
      setNotes([{ ...dataItem, inEdit: true }, ...notes])
    }
  }

  const handleEnterEdit = (item: NoteValue<T>) => {
    props.onEdit(item)
  }

  return {
    add: handleAdd,
    update: handleUpdate,
    remove: handleRemove,
    edit: handleEnterEdit,
    discard: handleDiscard,
    addNew: handleAddNew,
    itemChange: handleItemChange,
    cancel: handleCancel,
    isLoading: !props.notes,
    notes,
    setNotes,
    setNote,
    setEditNote,
  }
}
